import { createContext, useContext, useMemo, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const ThemeContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);

const getInitialMode = () => {
  const savedMode = localStorage.getItem("themeMode");
  if (savedMode) {
    return savedMode;
  }
  const prefersDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDarkMode ? "dark" : "light";
};

export const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode);

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", newMode);
      return newMode;
    });
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === "light"
            ? {
              background: {
                default: "#f5f5f5", // Keep light gray background
                paper: "#ffffff", // Keep white paper
              },
              text: {
                primary: "#4a4a4a", // Softer dark gray (less harsh than #676767)
                secondary: "#6b6b6b", // Muted gray (less intense than #555555)
              },
            }
            : {
              background: {
                default: "#212121", // Keep dark background
                paper: "#1B1B22", // Keep dark paper
              },
              text: {
                primary: "#d1d1d1", // Soft light gray (less bright than #f3f3f3)
                secondary: "#a0a0a0", // Dimmer gray (less intense than #bbbbbb)
              },
            }),
          primary: {
            main: mode === "light" ? "#1976d2" : "#90caf9", // Softer blue for dark mode
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
